// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Formatting helpers. All display numbers flow through here. */

export function formatNumber(n: number | null | undefined, dp = 0): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function formatPercent(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

/** Tonnes → a compact human string: 25.2 Mt, 640 kt, 8,900 t. */
export function formatTonnes(t: number | null | undefined): string {
  if (t == null || !Number.isFinite(t)) return '—';
  const a = Math.abs(t);
  if (a >= 1e6) return `${(t / 1e6).toFixed(a >= 1e7 ? 1 : 2)} Mt`;
  if (a >= 1e3) return `${(t / 1e3).toFixed(a >= 1e5 ? 0 : 0)} kt`;
  return `${Math.round(t).toLocaleString('en-AU')} t`;
}

/** Millions of tonnes with fixed 1-dp, for axes/labels. */
export function formatMt(t: number | null | undefined, dp = 1): string {
  if (t == null || !Number.isFinite(t)) return '—';
  return `${(t / 1e6).toFixed(dp)}`;
}

/** Financial-year label "2020-2021" → "2020–21". */
export function fyShort(yr: string): string {
  const m = /^(\d{4})-(\d{4})$/.exec(yr);
  if (!m) return yr;
  return `${m[1]}–${m[2].slice(2)}`;
}

export function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

/** Escape for a data-tip attribute value: newlines become &#10; for innerHTML,
 *  but setAttribute takes raw \n. Use this only when building innerHTML strings. */
export function tipAttr(s: string): string {
  return escapeHtml(s).replace(/\n/g, '&#10;');
}
